import React from "react";
import {
  availRed,
  availAmber,
  freshRed,
  freshAmber,
  wasteRed,
  wasteAmber
} from "./variable-vault";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowUp, faArrowDown } from "@fortawesome/pro-solid-svg-icons";
import { BakeryItem } from "../types";

const getArea = (isFOH: boolean) => {
  return isFOH ? "FOH" : "BOH";
};

export { getArea };

const getPercentage = (decimal: number | null) => {
  return decimal ? (decimal * 100).toFixed(0) + "%" : null;
};

export { getPercentage };

const postfixPercentage = (decimal: number | null) => {
  return decimal ? decimal.toFixed(0) + "%" : null;
};

export { postfixPercentage };

export const convertToHours = (totalMinutes: number | null) => {
  if (totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toFixed(0)
      .padStart(2, "0")}`;
  } else {
    return null;
  }
};

export const getMetricColor = (type: string, value: number | null) => {
  if (value) {
    if (type === "waste") {
      return value > wasteRed ? "red" : value > wasteAmber ? "amber" : "green";
    } else if (type === "avail") {
      return value < availRed ? "red" : value < availAmber ? "amber" : "green";
    } else if (type === "time") {
      return value > freshRed ? "red" : value > freshAmber ? "amber" : "green";
    }
  }
};

export const getTrend = (
  base: number | null,
  diff: number | null,
  upGood: boolean
) => {
  if (base && diff) {
    if (diff > 0) {
      return (
        <FontAwesomeIcon
          icon={faArrowUp as IconProp}
          className={upGood ? "fill-green" : "fill-red"}
        />
      );
    } else {
      return (
        <FontAwesomeIcon
          icon={faArrowDown as IconProp}
          className={upGood ? "fill-red" : "fill-green"}
        />
      );
    }
  }
};

export const convertTo12Hr = (hour: number) => {
  if (hour >= 13) {
    return (hour - 12).toString();
  } else if (hour === 12) {
    return hour?.toString();
  } else {
    return hour?.toString();
  }
};

export const getAmPm = (hour: number) => {
  if (hour >= 12) {
    return "pm";
  } else if (hour != null) {
    return "am";
  }
};

export const getTimeStamp = () => {
  const now = getUKDate();
  const hours = ("0" + now.getHours()).slice(-2);
  const minutes = ("0" + now.getMinutes()).slice(-2);
  const seconds = ("0" + now.getSeconds()).slice(-2);
  const milliseconds = ("00" + now.getMilliseconds()).slice(-3);
  return `${hours}:${minutes}:${seconds}:${milliseconds}`;
};

export const currentDateTime = () => {
  const localDateTime = getUKDate();
  return `${localDateTime.getFullYear()}-${String(
    localDateTime.getMonth() + 1
  ).padStart(2, "0")}-${String(localDateTime.getDate()).padStart(
    2,
    "0"
  )} ${String(localDateTime.getHours()).padStart(2, "0")}:${String(
    localDateTime.getMinutes()
  ).padStart(2, "0")}:${String(localDateTime.getSeconds()).padStart(2, "0")}`;
};

export const isInTargetWindow = (startHour: number, endHour: number) => {
  const now = getUKDate();
  return now.getHours() >= startHour && now.getHours() < endHour;
};

export const isBeforeTargetWindow = (startHour: number) => {
  const now = getUKDate();
  return now.getHours() < startHour - 1;
};

export const isPastTargetWindow = (endHour: number | null) => {
  if (endHour) {
    const now = getUKDate();
    return now.getHours() >= endHour;
  } else {
    return false;
  }
};

export const isFresh = (targetFresh: number, sentDate: number) => {
  return targetFresh ? targetFresh - (Date.now() - sentDate) / 60000 > 0 : true;
};

export const getOffset = (currentHour: number, i: number) => {
  let offsetClass;
  if (currentHour < 8) {
    offsetClass = i - 10 > 0 ? i - 10 : 0;
  } else if (currentHour < 11) {
    const offset =
      i - currentHour - 2 > 0
        ? i - currentHour - 2
        : (i - currentHour + 2) * -1;
    offsetClass = offset > 0 ? offset : 0;
  } else if (currentHour > 16) {
    offsetClass = 14 - i > 0 ? 14 - i : 0;
  } else {
    const offset =
      i - currentHour - 2 > 0
        ? i - currentHour - 2
        : (i - currentHour + 2) * -1;
    offsetClass = offset > 0 ? offset : 0;
  }

  if (currentHour > 8 && currentHour < 16) {
    return offsetClass + 1;
  } else {
    return offsetClass;
  }
};

export const checkFilter = (
  name: string,
  category: string,
  searchQueryState: string
) => {
  if (searchQueryState != "") {
    return name.toLowerCase().includes(searchQueryState.toLowerCase()) ||
      category.toLowerCase().includes(searchQueryState.toLowerCase())
      ? "filter-keep"
      : "filter-out";
  } else {
    return "filter-keep";
  }
};

export const isCurrentHour = (hour: number, currentHour: number) => {
  if (currentHour > hour) {
    return "past";
  } else if (currentHour === hour) {
    return "present";
  } else {
    return "future";
  }
};

interface Item {
  TotalPlanned: number;
  ProductId: number;
  [key: `H${number}`]: number;
}

export const getPLUPlan = (PLU: number, productPlanner: Item[]) => {
  return productPlanner.filter((item: Item) => {
    return item.ProductId === PLU;
  })[0];
};

export const getSentInHour = (item: BakeryItem, i: number) => {
  const key = `H${i}` as keyof BakeryItem;
  return item[key];
};

export const isPast = (end_time: string) => {
  const [hours, minutes] = end_time.split(":").map(Number);
  const now = getUKDate();
  const date = getUKDate();

  date.setHours(hours, minutes, 0, 0);

  return date < now;
};

export const getUKDate = () => {
  const now = new Date();
  
  const ukDate = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(now);
  
  const [datePart, timePart] = ukDate.split(', ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [hours, minutes, seconds] = timePart.split(':').map(Number);
  
  return new Date(year, month - 1, day, hours, minutes, seconds);
};

export const getDay = () => {
  return getUKDate().toLocaleDateString("en-GB", {
    weekday: "long"
  });
};
